(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const parseJSON = (node, fallback = []) => {
    try {
      return JSON.parse(node?.textContent || '') || fallback;
    } catch (error) {
      console.error('AF product data could not be read.', error);
      return fallback;
    }
  };

  const initProduct = (root) => {
    if (!root || root.dataset.afpReady === 'true') return;
    root.dataset.afpReady = 'true';

    const gallery = root.querySelector('[data-afp-gallery]');
    const viewport = root.querySelector('[data-afp-gallery-viewport]');
    const frames = [...root.querySelectorAll('[data-afp-media-frame]')];
    const thumbs = [...root.querySelectorAll('[data-afp-media-thumb]')];
    const progress = root.querySelector('[data-afp-gallery-progress]');
    const count = root.querySelector('[data-afp-gallery-count]');
    const variants = parseJSON(root.querySelector('[data-afp-variants]'));
    const form = root.querySelector('[data-afp-product-form]');
    const variantSelect = root.querySelector('[data-afp-variant-select]');
    const optionGroups = [...root.querySelectorAll('[data-afp-option-group]')];
    const optionRadios = [...root.querySelectorAll('[data-afp-option-radio]')];
    const price = root.querySelector('[data-afp-price]');
    const comparePrice = root.querySelector('[data-afp-compare-price]');
    const buttonPrice = root.querySelector('[data-afp-button-price]');
    const mobilePrice = root.querySelector('[data-afp-mobile-price]');
    const availability = root.querySelector('[data-afp-availability]');
    const addButton = root.querySelector('[data-afp-add]');
    const addLabel = root.querySelector('[data-afp-add-label]');
    const mobileButton = root.querySelector('[data-afp-mobile-submit]');
    const mobileLabel = root.querySelector('[data-afp-mobile-label]');
    const mobileBar = root.querySelector('[data-afp-mobile-buy]');
    const status = root.querySelector('[data-afp-status]');
    const quantityInput = root.querySelector('[data-afp-quantity-input]');
    const currency = root.dataset.currency || 'USD';
    const availableText = availability?.textContent?.trim() || 'READY TO SHIP';
    const availableAddText = addLabel?.textContent?.trim() || 'ADD TO BAG';

    let activeMediaId = root.querySelector('.afp-gallery__frame.is-active')?.dataset.afpMediaFrame || frames[0]?.dataset.afpMediaFrame || null;
    let currentVariant = null;

    const setHTML = (element, html) => {
      if (element) element.innerHTML = html || '';
    };


    const formatMoney = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(Number(cents || 0) / 100);
      } catch (_) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    };

    const quantityValue = () => Math.max(1, Number(quantityInput?.value || 1));

    const updateQuantityTotal = () => {
      if (!currentVariant) return;
      const total = Number(currentVariant.price || 0) * quantityValue();
      setHTML(buttonPrice, formatMoney(total));
      setHTML(mobilePrice, formatMoney(total));
    };

    const showMedia = (mediaId, animate = true) => {
      if (!frames.length) return;

      let index = frames.findIndex((frame) => String(frame.dataset.afpMediaFrame) === String(mediaId));
      if (index < 0) index = 0;
      const activeFrame = frames[index];
      activeMediaId = activeFrame.dataset.afpMediaFrame;

      frames.forEach((frame) => {
        const isActive = frame === activeFrame;
        frame.classList.toggle('is-active', isActive);
        frame.setAttribute('aria-hidden', String(!isActive));
        if (!isActive) frame.querySelector('video')?.pause();
      });

      thumbs.forEach((thumb) => {
        const isActive = String(thumb.dataset.afpMediaThumb) === String(activeMediaId);
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', String(isActive));
        if (isActive && !reducedMotion.matches) {
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      if (progress) progress.style.width = `${((index + 1) / frames.length) * 100}%`;
      if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(frames.length).padStart(2, '0')}`;

      if (animate && gallery && !reducedMotion.matches) {
        gallery.classList.remove('is-changing');
        requestAnimationFrame(() => gallery.classList.add('is-changing'));
        window.setTimeout(() => gallery.classList.remove('is-changing'), 700);
      }
    };

    const moveMedia = (direction) => {
      if (frames.length < 2) return;
      const currentIndex = Math.max(0, frames.findIndex((frame) => String(frame.dataset.afpMediaFrame) === String(activeMediaId)));
      const nextIndex = (currentIndex + direction + frames.length) % frames.length;
      showMedia(frames[nextIndex].dataset.afpMediaFrame);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => showMedia(thumb.dataset.afpMediaThumb));
    });
    root.querySelector('[data-afp-gallery-prev]')?.addEventListener('click', () => moveMedia(-1));
    root.querySelector('[data-afp-gallery-next]')?.addEventListener('click', () => moveMedia(1));

    if (viewport && frames.length > 1) {
      let touchStartX = 0;
      viewport.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.clientX || 0;
      }, { passive: true });
      viewport.addEventListener('touchend', (event) => {
        const distance = (event.changedTouches[0]?.clientX || 0) - touchStartX;
        if (Math.abs(distance) > 45) moveMedia(distance > 0 ? -1 : 1);
      }, { passive: true });
    }

    const selectedOptions = () => optionGroups.map((group) => group.querySelector('input:checked')?.value || '');

    const findSelectedVariant = () => {
      const chosen = selectedOptions();
      if (!chosen.length) {
        return variants.find((variant) => String(variant.id) === String(variantSelect?.value)) || variants[0] || null;
      }
      return variants.find((variant) => variant.options?.every((value, index) => value === chosen[index])) || null;
    };

    const refreshChoiceUI = () => {
      optionGroups.forEach((group) => {
        const checked = group.querySelector('input:checked');
        const selectedText = group.querySelector('[data-afp-option-selected]');
        if (selectedText && checked) selectedText.textContent = checked.value;
        group.querySelectorAll('.afp-choice').forEach((choice) => {
          choice.classList.toggle('is-selected', Boolean(checked && choice.contains(checked)));
        });
      });
    };

    const updateVariant = (variant, updateURL = true) => {
      refreshChoiceUI();

      if (!variant) {
        currentVariant = null;
        if (addButton) addButton.disabled = true;
        if (mobileButton) mobileButton.disabled = true;
        if (addLabel) addLabel.textContent = 'UNAVAILABLE';
        if (mobileLabel) mobileLabel.textContent = 'UNAVAILABLE';
        if (availability) availability.textContent = 'UNAVAILABLE';
        if (status) {
          status.textContent = 'This combination is unavailable. Choose another option.';
          status.classList.add('is-error');
        }
        return;
      }

      currentVariant = variant;
      if (variantSelect) variantSelect.value = String(variant.id);
      setHTML(price, variant.price_html);
      updateQuantityTotal();

      if (comparePrice) {
        setHTML(comparePrice, variant.compare_html);
        comparePrice.hidden = !variant.compare_html;
      }

      const label = variant.available ? availableAddText : 'SOLD OUT';
      if (addButton) addButton.disabled = !variant.available;
      if (mobileButton) mobileButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = label;
      if (mobileLabel) mobileLabel.textContent = label;
      if (availability) availability.textContent = variant.available ? availableText : 'SOLD OUT';

      if (status) {
        status.textContent = variant.available ? '' : 'This option is currently sold out.';
        status.classList.toggle('is-error', !variant.available);
      }

      if (variant.media_id) showMedia(variant.media_id);

      if (updateURL && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    optionRadios.forEach((radio) => {
      radio.addEventListener('change', () => updateVariant(findSelectedVariant()));
    });

    const commitQuantity = (nextValue) => {
      if (!quantityInput) return;
      quantityInput.value = String(Math.max(Number(quantityInput.min || 1), Number(nextValue || 1)));
      updateQuantityTotal();
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    root.querySelector('[data-afp-qty-minus]')?.addEventListener('click', () => {
      commitQuantity(quantityValue() - 1);
    });
    root.querySelector('[data-afp-qty-plus]')?.addEventListener('click', () => {
      commitQuantity(quantityValue() + 1);
    });
    quantityInput?.addEventListener('input', updateQuantityTotal);
    quantityInput?.addEventListener('change', updateQuantityTotal);

    const refreshHeaderCount = async () => {
      try {
        const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        document.querySelectorAll('.af-header__bag span, [data-cart-count], .cart-count-bubble span:first-child').forEach((badge) => {
          badge.textContent = String(cart.item_count);
          badge.hidden = false;
        });
      } catch (_) {
        // Cart was added successfully; a badge refresh failure should not block checkout.
      }
    };

    form?.addEventListener('submit', async (event) => {
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      if (!addButton || addButton.disabled) return;

      addButton.setAttribute('aria-busy', 'true');
      addButton.disabled = true;
      if (status) {
        status.textContent = 'Adding your selected option…';
        status.classList.remove('is-error');
      }

      try {
        const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
        const cartDrawer = document.querySelector('cart-drawer');
        const formData = new FormData(form);

        if (cartDrawer) {
          const sectionIds = typeof cartDrawer.getSectionsToRender === 'function'
            ? cartDrawer.getSectionsToRender().map((section) => section.id)
            : ['cart-drawer', 'cart-icon-bubble'];
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const payload = await response.json().catch(() => ({}));

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || payload.errors || 'Unable to add this option.');
        }

        if (cartDrawer && payload.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(payload);
        } else {
          await refreshHeaderCount();
          const toast = document.querySelector('[data-af-cart-toast]');
          const toastText = toast?.querySelector('span');
          if (toastText) toastText.textContent = 'Added to your ritual';
          toast?.classList.add('is-visible');
          window.setTimeout(() => toast?.classList.remove('is-visible'), 3400);
        }

        if (status) status.textContent = 'Added to your bag.';
      } catch (error) {
        console.error(error);
        if (status) {
          status.textContent = error.message || 'The item could not be added. Please try again.';
          status.classList.add('is-error');
        }
      } finally {
        addButton.removeAttribute('aria-busy');
        const currentVariant = findSelectedVariant();
        addButton.disabled = !currentVariant?.available;
      }
    });

    mobileButton?.addEventListener('click', () => form?.requestSubmit());

    if (mobileBar && form) {
      const updateMobileBar = () => {
        const formRect = form.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        mobileBar.classList.toggle('is-visible', formRect.bottom < 0 && rootRect.bottom > window.innerHeight);
      };
      window.addEventListener('scroll', updateMobileBar, { passive: true });
      window.addEventListener('resize', updateMobileBar);
      updateMobileBar();
    }

    if (viewport && !reducedMotion.matches && window.matchMedia('(pointer:fine)').matches) {
      viewport.addEventListener('pointermove', (event) => {
        const rect = viewport.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - .5) * 10;
        const y = ((event.clientY - rect.top) / rect.height - .5) * 10;
        viewport.style.setProperty('--afp-image-x', `${x.toFixed(2)}px`);
        viewport.style.setProperty('--afp-image-y', `${y.toFixed(2)}px`);
      }, { passive: true });
      viewport.addEventListener('pointerleave', () => {
        viewport.style.setProperty('--afp-image-x', '0px');
        viewport.style.setProperty('--afp-image-y', '0px');
      });
    }

    showMedia(activeMediaId, false);
    updateVariant(findSelectedVariant(), false);
    requestAnimationFrame(() => root.classList.add('is-ready'));
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-afp-product]').forEach(initProduct);
  };

  document.addEventListener('DOMContentLoaded', () => initialise());
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
